import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { Component, Input, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ExceptionsDTO } from 'src/app/shared/models/dtos/exceptions-dto/exceptions-dto';
import { DetailedRegion } from 'src/app/shared/models/dtos/region/detailed-region';

/**
 * Data transfer object for a selected region.
 */
export class SelectedRegion extends ExceptionsDTO {
  collapsed?: boolean;
  partialSelected?: boolean;
}

@Component({
  selector: 'app-checkbox-accordion',
  templateUrl: './checkbox-accordion.component.html',
  styleUrls: ['./checkbox-accordion.component.scss'],
  animations: [
    trigger('rotateIcon', [
      state('collapsed', style({ transform: 'rotate(0deg)' })),
      state('expanded', style({ transform: 'rotate(180deg)' })),
      transition('collapsed <=> expanded', animate('300ms ease-in-out')),
    ]),
    trigger('accordion', [
      transition(':enter', [
        style({ height: 0, opacity: 0 }),
        animate('300ms ease-out', style({ height: '*', opacity: 1 })),
      ]),
      transition(':leave', [
        style({ height: '*', opacity: 1 }),
        animate('300ms ease-in', style({ height: 0, opacity: 0 })),
      ]),
    ]),
  ],
})
/**
 * CheckboxAccordionComponent is a component that displays a checkbox accordion
 * for regions and countries. It allows selecting countries individually or by region,
 * and emits the selected country IDs to the parent.
 */
export class CheckboxAccordionComponent implements OnInit {
  @Input() regionList: DetailedRegion[] = [];
  @Input() formGroup!: FormGroup;

  selectedRegions: SelectedRegion[] = [];

  /**
   * On init
   */
  ngOnInit(): void {
    this.initializeSelectedRegions();
  }

  /**
   * Initializes selectedRegions based on the input regionList.
   */
  private initializeSelectedRegions(): void {
    const regionExceptionsControl = this.formGroup.get('regionExceptions');
    const formExceptions = regionExceptionsControl?.value || [];

    this.selectedRegions = this.regionList.map((region) => {
      const formRegion = formExceptions.find(
        (entry: any) => entry.regionId === region.id
      );

      const allRegionCountries = region.countries.map((country) => ({
        id: country.id,
        name: country.name,
      }));

      const selectedCountries = formRegion?.countries || [];
      const allCountries =
        selectedCountries.length === allRegionCountries.length;
      const partialSelected = selectedCountries.length > 0 && !allCountries;

      return {
        regionId: region.id,
        name: region.name,
        countries: allCountries ? allRegionCountries : selectedCountries,
        allCountries,
        partialSelected,
        collapsed: false,
      };
    });

    this.onSelectionChange();
  }

  /**
   * Toggles the selection state of all countries in a region.
   * @param {number} regionId - The ID of the region to update.
   * @param {boolean} checked - Whether to select or deselect all.
   */
  toggleAll(regionId: number, checked: boolean): void {
    const region = this.getRegionById(regionId);
    const fullRegion = this.regionList.find((r) => r.id === regionId);

    if (!region || !fullRegion) return;

    const mappedCountries = fullRegion.countries.map((c) => ({
      id: c.id,
      name: c.name,
    }));

    region.countries = checked ? mappedCountries : [];
    region.allCountries = checked;
    region.partialSelected = false;

    this.onSelectionChange();
  }

  /**
   * Toggles selection of a specific country in a region.
   * Updates allCountries and partialSelected flags accordingly.
   * @param {number} regionId - The ID of the region.
   * @param {Country} country - The country to toggle.
   */
  toggleCountrySelection(
    regionId: number,
    country: { id: number; name: string }
  ): void {
    const region = this.getRegionById(regionId);
    const fullRegion = this.regionList.find((r) => r.id === regionId);

    if (!region || !fullRegion) return;

    const countryIndex = region.countries.findIndex((c) => c.id === country.id);

    if (countryIndex > -1) {
      region.countries.splice(countryIndex, 1);
    } else {
      region.countries.push({ id: country.id, name: country.name });
    }

    const total = fullRegion.countries.length;
    const selected = region.countries.length;

    region.allCountries = selected === total;
    region.partialSelected = selected > 0 && selected < total;

    this.onSelectionChange();
  }

  /**
   * Checks if a country is selected in the given region.
   * @param {number} regionId - The ID of the region.
   * @param {number} countryId - The ID of the country.
   * @return {boolean} True if the country is selected, false otherwise.
   */
  isCountrySelected(regionId: number, countryId: number): boolean {
    const region = this.getRegionById(regionId);
    return !!region?.countries.some((c) => c.id === countryId);
  }

  /**
   * Gets the SelectedRegionDto by ID.
   * @param {number} regionId - The region ID to find.
   * @return {SelectedRegion | undefined} The matching region or undefined.
   */
  getRegionById(regionId: number): SelectedRegion | undefined {
    return this.selectedRegions.find((r) => r.regionId === regionId);
  }

  /**
   * Toggles the collapsed state of a region.
   * @param {number} regionId - The region ID to toggle.
   */
  toggleCollapse(regionId: number): void {
    const region = this.getRegionById(regionId);
    if (region) region.collapsed = !region.collapsed;
  }

  /**
   * Handles selection changes:
   * - Updates the formGroup
   */
  onSelectionChange(): void {
    if (!this.formGroup) return;

    const selectedRegionsDto = this.selectedRegions
      .filter((r) => r.countries.length > 0)
      .map((r) => ({
        regionId: r.regionId,
        name: r.name,
        countries: r.countries,
        allCountries: r.allCountries,
      }));

    this.formGroup.patchValue({
      regionExceptions: selectedRegionsDto,
    });
  }
}
